import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  Theme,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import Layout, { PageLayout } from 'src/components/Layout';
import { BaseTabsHeader } from 'src/components/UI/BaseTab';
import { RouteContext } from 'src/context';
import history from 'src/history';
import { TableToolbar } from 'src/components/UI/Pages/TableToolbar';
import { PageToolbar, PageToolbarAction } from 'src/components/UI';
import { ensureUnreachable } from 'src/utils/CommonUtils';
import { SubmissionAndRequestTable } from 'src/components/Contracts/SubmissionAndRequestTable';
import { TableSearch } from 'src/components/SearchInput/Searchbox';
import { useGetContractTemplatesQuery } from 'src/services/api';
import { ShareContractModal } from 'src/components/Contracts/ShareContractModal';
import { CONTRACTS_PAGE } from 'src/constants';
import { BreadCrumbBase } from 'src/components/UI/BreadCrumbBase';

export enum ContractDetailsPageView {
  Edit = 'edit',
  SubmissionRequests = 'submissions_requests',
}

export type ContractDetailsPageQueryParams = {
  view: ContractDetailsPageView;
  templateId: string;
};

const PAGE_TAB_HEADERS: Array<{
  key: ContractDetailsPageView;
  label: string;
}> = [
  {
    key: ContractDetailsPageView.SubmissionRequests,
    label: 'Submissions and requests',
  },
  {
    key: ContractDetailsPageView.Edit,
    label: 'Edit',
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  breadcrumbWrapper: {
    [theme.breakpoints.down('xs')]: {
      width: 130,
    },
  },
}));

export const ContractDetailsPage = () => {
  const classes = useStyles();
  const { query } = useContext(RouteContext);
  const { view, templateId } = query as ContractDetailsPageQueryParams;

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const { data: templates } = useGetContractTemplatesQuery();

  const selectedTemplate = templates?.find(t => t.id === templateId);

  const [activeTabKey, setActiveTabKey] = useState(
    view || ContractDetailsPageView.SubmissionRequests,
  );

  const handleTabChange = (key: ContractDetailsPageView) => {
    setActiveTabKey(key);
    history.replace({ search: `templateId=${templateId}&view=${key}` });
  };

  const renderActiveTab = () => {
    switch (view) {
      case ContractDetailsPageView.SubmissionRequests:
        return (
          <SubmissionAndRequestTable selectedTemplate={selectedTemplate} />
        );
      case ContractDetailsPageView.Edit:
        return <div>Edit page</div>;
      default:
        return ensureUnreachable(view);
    }
  };

  const breadcrumbItems = useMemo(() => {
    if (isMobile) {
      return [
        {
          label: selectedTemplate?.name || '',
        },
      ];
    }

    return [
      {
        label: 'Contracts',
        path: CONTRACTS_PAGE.path,
      },
      {
        label: selectedTemplate?.name || '',
      },
    ];
  }, [isMobile, selectedTemplate]);

  return (
    <Layout
      headerComponent={
        <TableToolbar>
          <PageToolbar
            title={
              <div className={classes.breadcrumbWrapper}>
                <BreadCrumbBase items={breadcrumbItems} />
              </div>
            }
            actionButtons={[
              <PageToolbarAction
                actions={[]}
                createButton={
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setIsShareModalOpen(true)}
                  >
                    Share contract
                  </Button>
                }
              >
                <TableSearch />
              </PageToolbarAction>,
            ]}
          />
        </TableToolbar>
      }
    >
      <PageLayout routeAccessCheck="allowContractsPage" noSpace>
        <BaseTabsHeader
          tabs={PAGE_TAB_HEADERS}
          activeTabKey={activeTabKey}
          onTabChange={(_, key) =>
            handleTabChange(key as ContractDetailsPageView)
          }
        />
        {renderActiveTab()}
        <ShareContractModal
          open={isShareModalOpen}
          selectedTemplate={selectedTemplate}
          onClose={() => setIsShareModalOpen(false)}
        />
      </PageLayout>
    </Layout>
  );
};
